import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Skeleton from 'antd/lib/skeleton';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { LinkOutlined } from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import { copyToClipboard } from '@/helpers';
import { RootState } from '@/reducers/reducers';
import { selectAllAppsIdsInOrg, selectAppsById } from '@/selectors/selectors';
import { AppliedChange, AppliedPlan, useGetConfigAsCodeAppliedPlansQuery } from '@/middlewares/defaultApi';
import { antGrey, colorWithAlpha, rwColors } from '../styles/globalStyles';
import { structuredPatch } from 'diff';

const contextLineCount = 3;

const actionLabels: Record<AppliedChange['action'], string> = {
    create: 'Added',
    update: 'Updated',
    delete: 'Removed',
};

const plansPageSkeleton = (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {[0, 1, 2, 3, 4].map((index) => (
            <Skeleton.Input key={index} active block style={{ height: 40 }} />
        ))}
    </Space>
);

interface ConfigAsCodeHistoryProps {
    orgId: string;
}

export const ConfigAsCodeHistory: React.FC<ConfigAsCodeHistoryProps> = ({ orgId }) => {
    const history = useHistory();
    const location = useLocation();
    const queryParams = qs.parse(location.search);
    const appId = typeof queryParams.appId === 'string' ? queryParams.appId : undefined;
    const planId = typeof queryParams.planId === 'string' ? queryParams.planId : undefined;
    const appIds = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId), shallowEqual);
    const apps = useSelector((state: RootState) => selectAppsById(state, appIds), shallowEqual);
    const { appNamesById, appOptions } = React.useMemo(() => {
        const appNamesById: Record<string, string> = {};
        const appOptions = apps.map((app) => {
            appNamesById[app.id] = app.appSettings.appName;
            return { value: app.id, label: app.appSettings.appName };
        });
        return { appNamesById, appOptions };
    }, [apps]);

    // Same args as the first page, so RTK Query serves both from one request
    const { data: firstPage } = useGetConfigAsCodeAppliedPlansQuery({ orgId, appId, planId });

    const hasNoAppliedPlans =
        !appId && !planId && firstPage?.data?.plans.length === 0 && !firstPage.data.paginationInfo.hasMore;

    if (hasNoAppliedPlans) {
        return (
            <Text style={{ display: 'block', marginTop: 12 }}>
                No configuration file changes have been applied yet.
            </Text>
        );
    }

    return (
        <Space
            direction="vertical"
            size={16}
            style={{ width: '100%', maxWidth: 1000, marginTop: 12, paddingBottom: 48 }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
                <Form layout="vertical" style={{ display: 'flex', gap: 16 }}>
                    <Form.Item label="App" style={{ marginBottom: 0 }}>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="All apps"
                            style={{ width: 280 }}
                            value={appId}
                            options={appOptions}
                            onChange={(value?: string) =>
                                history.replace({ search: qs.stringify({ appId: value, planId }) })
                            }
                        />
                    </Form.Item>
                    <Form.Item label="Plan ID" style={{ marginBottom: 0 }}>
                        <Input.Search
                            key={planId}
                            allowClear
                            placeholder="Search by plan ID"
                            style={{ width: 320 }}
                            defaultValue={planId}
                            onSearch={(value) =>
                                history.replace({ search: qs.stringify({ appId, planId: value.trim() || undefined }) })
                            }
                        />
                    </Form.Item>
                </Form>
            </div>
            <AppliedPlansList
                key={`${appId}-${planId}`}
                orgId={orgId}
                appId={appId}
                planId={planId}
                appNamesById={appNamesById}
            />
        </Space>
    );
};

interface AppliedPlansListProps {
    orgId: string;
    appId?: string;
    planId?: string;
    appNamesById: Record<string, string>;
}

// Each page is its own cached query, so loading more never refetches or merges earlier pages
const AppliedPlansList: React.FC<AppliedPlansListProps> = (props) => {
    const [cursors, setCursors] = React.useState<string[]>(['']);

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {cursors.map((cursor, index) => (
                <AppliedPlansPage
                    key={cursor}
                    {...props}
                    cursor={cursor}
                    isFirstPage={index === 0}
                    onLoadMore={
                        index === cursors.length - 1 ? (next) => setCursors((current) => [...current, next]) : undefined
                    }
                />
            ))}
        </Space>
    );
};

interface AppliedPlansPageProps extends AppliedPlansListProps {
    cursor: string;
    isFirstPage: boolean;
    onLoadMore?: (nextCursor: string) => void;
}

const AppliedPlansPage: React.FC<AppliedPlansPageProps> = ({
    orgId,
    appId,
    planId,
    appNamesById,
    cursor,
    isFirstPage,
    onLoadMore,
}) => {
    const { data, isLoading, isError } = useGetConfigAsCodeAppliedPlansQuery({
        orgId,
        appId,
        planId,
        cursor: cursor || undefined,
    });

    if (isLoading) {
        return plansPageSkeleton;
    }
    if (isError || !data?.data) {
        return <Text type="danger">Failed to load configuration file changes. Please try again.</Text>;
    }

    const { plans, paginationInfo } = data.data;
    if (isFirstPage && plans.length === 0 && !paginationInfo.hasMore) {
        return <Text>No configuration file changes match these filters.</Text>;
    }

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {plans.map((plan) => (
                <Collapse key={plan.planId} defaultActiveKey={plan.planId === planId ? [plan.planId] : []}>
                    <Collapse.Panel
                        key={plan.planId}
                        header={<AppliedPlanHeader plan={plan} appName={appNamesById[plan.appId]} />}
                        extra={
                            <Space size={12}>
                                <Tooltip title={dayjs(plan.appliedAt).format('MMM D, YYYY [at] h:mm A')}>
                                    <Text type="secondary">
                                        Applied {dayjs(plan.appliedAt).fromNow()}
                                        {plan.appliedBy ? ` by ${plan.appliedBy}` : ''}
                                    </Text>
                                </Tooltip>
                                <CopyPlanLinkButton plan={plan} />
                            </Space>
                        }
                    >
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Text type="secondary">
                                Plan ID:{' '}
                                <Text code copyable>
                                    {plan.planId}
                                </Text>
                            </Text>
                            {plan.changes.map((change) => (
                                <ChangeDiff key={change.path} change={change} />
                            ))}
                        </Space>
                    </Collapse.Panel>
                </Collapse>
            ))}
            {onLoadMore && paginationInfo.nextCursor ?
                <div style={{ textAlign: 'center' }}>
                    <Button type="default" size="small" onClick={() => onLoadMore(paginationInfo.nextCursor ?? '')}>
                        Load more
                    </Button>
                </div>
            :   null}
        </Space>
    );
};

const AppliedPlanHeader: React.FC<{ plan: AppliedPlan; appName?: string }> = ({ plan, appName }) => {
    let addedLines = 0;
    let removedLines = 0;
    for (const change of plan.changes) {
        for (const hunk of getChangeHunks(change)) {
            for (const line of hunk.lines) {
                if (line.startsWith('+')) {
                    addedLines++;
                } else if (line.startsWith('-')) {
                    removedLines++;
                }
            }
        }
    }

    return (
        <Space size={16} wrap>
            <Text strong>{appName ?? plan.appId}</Text>
            {/* Fixed widths keep the columns lined up across rows */}
            <Text type="secondary" style={{ display: 'inline-block', minWidth: 64 }}>
                {plan.changes.length} {plan.changes.length === 1 ? 'change' : 'changes'}
            </Text>
            <Space size={4} style={{ minWidth: 64 }}>
                <Text style={{ color: rwColors.statusGreen }}>+{addedLines}</Text>
                <Text style={{ color: rwColors.statusRed }}>-{removedLines}</Text>
            </Space>
        </Space>
    );
};

// Same link the apply notification sends, so it opens this plan expanded
const CopyPlanLinkButton: React.FC<{ plan: AppliedPlan }> = ({ plan }) => (
    <Tooltip title="Copy link">
        <Button
            type="text"
            size="small"
            icon={<LinkOutlined />}
            aria-label="Copy link to this change"
            onClick={(event) => {
                // Keeps the click from toggling the panel
                event.stopPropagation();
                const search = qs.stringify({ appId: plan.appId, planId: plan.planId });
                copyToClipboard(`${window.location.origin}${window.location.pathname}?${search}`);
            }}
        />
    </Tooltip>
);

const ChangeDiff: React.FC<{ change: AppliedChange }> = ({ change }) => (
    <div
        style={{
            border: `1px solid ${antGrey.grey4}`,
            borderRadius: 6,
            overflow: 'hidden',
            fontFamily: 'monospace',
            fontSize: 12,
        }}
    >
        <div
            style={{
                padding: '6px 12px',
                backgroundColor: antGrey.grey2,
                borderBottom: `1px solid ${antGrey.grey4}`,
            }}
        >
            <Space size={8}>
                <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {change.path}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {actionLabels[change.action]}
                </Text>
            </Space>
        </div>
        {getChangeHunks(change).map((hunk, hunkIndex) => (
            <React.Fragment key={hunk.oldStart}>
                {hunkIndex > 0 ?
                    <div style={{ padding: '0 12px', backgroundColor: antGrey.grey2 }}>⋯</div>
                :   null}
                {hunk.lines.map((line, index) => (
                    <DiffLine key={index} line={line} />
                ))}
            </React.Fragment>
        ))}
    </div>
);

const DiffLine: React.FC<{ line: string }> = ({ line }) => {
    const sign = line[0];
    const color =
        sign === '+' ? rwColors.statusGreen
        : sign === '-' ? rwColors.statusRed
        : undefined;

    return (
        <div
            style={{
                padding: '0 12px',
                whiteSpace: 'pre-wrap',
                backgroundColor: color ? colorWithAlpha(color, 0.12) : undefined,
            }}
        >
            <span style={{ color, userSelect: 'none' }}>{sign} </span>
            {line.slice(1)}
        </div>
    );
};

// Every line ends in a newline, so the last line isn't reported as changed just because more lines follow it
function getChangeHunks(change: AppliedChange) {
    const oldText = change.removedLines.map((line) => `${line}\n`).join('');
    const newText = change.addedLines.map((line) => `${line}\n`).join('');
    const { hunks } = structuredPatch('', '', oldText, newText, '', '', { context: contextLineCount });
    // jsdiff flags an empty side (a created or deleted field) as missing its final newline
    return hunks.map((hunk) => ({ ...hunk, lines: hunk.lines.filter((line) => !line.startsWith('\\')) }));
}
